/**
 * One module's submissions, for its mentor: each assessment's results (per
 * student and per question) and each assignment's submissions, with review.
 *
 * "Active time" is the span from a student's first to last activity on an
 * assessment's questions, not time spent on the page.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

import * as subsApi from "./submissionsApi";
import { ago, date, duration, fileSize, number, percent, plural } from "./format";
import { FONT, INK, Kpi, KpiRow, Meter, StatusChip } from "./parts";

const TYPE_LABEL = { p: "Programming", s: "Subjective", o: "MCQ" };
const cell = { fontSize: 13, py: 1 };
const head = { fontSize: 12, color: INK.muted, fontWeight: 600, py: 1, whiteSpace: "nowrap" };

const Card = ({ children }) => (
  <Box sx={{ border: "1px solid #d6e6f2", borderRadius: "12px", p: 2, mb: 2.5, background: "#fbfdff" }}>{children}</Box>
);

const Empty = ({ children }) => (
  <Box sx={{ p: 4, textAlign: "center", color: INK.muted, border: "1px dashed #cbd5e1", borderRadius: "12px" }}>
    {children}
  </Box>
);

/** Sort rows by a column; nulls last either way. */
const useSort = (rows, initial) => {
  const [sort, setSort] = useState(initial);
  const sorted = useMemo(() => {
    const { key, dir } = sort;
    return [...rows].sort((a, b) => {
      const x = a[key];
      const y = b[key];
      if (x === y) return 0;
      if (x === null || x === undefined) return 1;
      if (y === null || y === undefined) return -1;
      const c = typeof x === "string" ? x.localeCompare(y) : x - y;
      return dir === "asc" ? c : -c;
    });
  }, [rows, sort]);
  const by = (key, label, defaultDir = "desc") => (
    <TableSortLabel
      active={sort.key === key}
      direction={sort.key === key ? sort.dir : defaultDir}
      onClick={() => setSort({ key, dir: sort.key === key && sort.dir === defaultDir ? (defaultDir === "asc" ? "desc" : "asc") : defaultDir })}
    >
      {label}
    </TableSortLabel>
  );
  return [sorted, by];
};

// ─── Assessments ───────────────────────────────────────────────────────────

const StudentsTable = ({ assessment, names, isCohort }) => {
  const navigate = useNavigate();
  const rows = useMemo(
    () => assessment.students.map((r) => ({ ...r, name: names[r.student_id] || "Student" })),
    [assessment, names],
  );
  const [sorted, by] = useSort(rows, { key: "name", dir: "asc" });
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" aria-label={`${assessment.title}: students`}>
        <TableHead>
          <TableRow>
            {isCohort && <TableCell sx={head}>{by("name", "Student", "asc")}</TableCell>}
            <TableCell sx={head}>Status</TableCell>
            <TableCell sx={head}>{by("answered", "Answered")}</TableCell>
            <TableCell sx={head}>{by("percent", "Score")}</TableCell>
            <TableCell sx={head}>{by("attempts", "Attempts")}</TableCell>
            <TableCell sx={head}>
              <Tooltip title="From the student's first to last activity on these questions">
                <span>{by("active_seconds", "Active time")}</span>
              </Tooltip>
            </TableCell>
            <TableCell sx={head}>{by("last_activity", "Last activity")}</TableCell>
            <TableCell sx={head}>{by("awaiting_review", "To review")}</TableCell>
            <TableCell sx={head} />
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((r) => (
            <TableRow key={r.student_id} hover data-testid="student-row">
              {isCohort && <TableCell sx={{ ...cell, fontWeight: 500 }}>{r.name}</TableCell>}
              <TableCell sx={cell}><StatusChip status={r.status} /></TableCell>
              <TableCell sx={cell}>{r.answered}/{r.total}</TableCell>
              <TableCell sx={cell}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Meter value={r.percent} label={`${number(r.score)} of ${number(r.max_score)} points`} />
                  <Typography component="span" sx={{ fontSize: 12, color: INK.muted, whiteSpace: "nowrap" }}>
                    {number(r.score)}/{number(r.max_score)}
                  </Typography>
                </Box>
                {r.unmarked_points > 0 && (
                  <Typography sx={{ fontSize: 11, color: INK.muted, whiteSpace: "nowrap" }}>
                    {number(r.unmarked_points)} pts not marked yet
                    {r.marked_percent !== null ? ` · ${percent(r.marked_percent)} of marked` : ""}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={cell}>{r.answered ? r.attempts : "—"}</TableCell>
              <TableCell sx={cell}>{duration(r.active_seconds)}</TableCell>
              <TableCell sx={cell}>{ago(r.last_activity)}</TableCell>
              <TableCell sx={cell}>{r.awaiting_review || "—"}</TableCell>
              <TableCell sx={cell} align="right">
                {r.answered > 0 && (
                  <Button size="small" onClick={() => navigate(subsApi.reviewAnswersPath(r))}
                          sx={{ textTransform: "none", whiteSpace: "nowrap" }}>
                    {r.awaiting_review ? "Review answers" : "See answers"} →
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const QuestionsTable = ({ assessment }) => (
  <Box sx={{ overflowX: "auto" }}>
    <Table size="small" aria-label={`${assessment.title}: questions`}>
      <TableHead>
        <TableRow>
          <TableCell sx={head}>#</TableCell>
          <TableCell sx={head}>Question</TableCell>
          <TableCell sx={head}>Answered</TableCell>
          <TableCell sx={head}>
            <Tooltip title="MCQ: chose the right option. Programming: passed every test case.">
              <span>Correct</span>
            </Tooltip>
          </TableCell>
          <TableCell sx={head}>Avg score</TableCell>
          <TableCell sx={head}>Avg attempts</TableCell>
          <TableCell sx={head}>
            <Tooltip title="Programming: median time from first activity to passing every test case">
              <span>Time to pass</span>
            </Tooltip>
          </TableCell>
          <TableCell sx={head}>To review</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {assessment.questions.map((q, i) => (
          <TableRow key={`${q.type}-${q.id}`} hover data-testid="question-row">
            <TableCell sx={cell}>{i + 1}</TableCell>
            <TableCell sx={{ ...cell, maxWidth: 320 }}>
              <Typography sx={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {q.title}
              </Typography>
              <Typography sx={{ fontSize: 11, color: INK.muted }}>{TYPE_LABEL[q.type]} · {q.max_score} pts</Typography>
            </TableCell>
            <TableCell sx={cell}><Meter value={q.answered_percent} label={`${q.answered} answered`} /></TableCell>
            <TableCell sx={cell}>{q.type === "s" ? "—" : <Meter value={q.correct_percent} />}</TableCell>
            <TableCell sx={cell}>{percent(q.average_percent)}</TableCell>
            <TableCell sx={cell}>{number(q.average_attempts)}</TableCell>
            <TableCell sx={cell}>{q.type === "p" ? duration(q.median_time_to_pass_seconds) : "—"}</TableCell>
            <TableCell sx={cell}>{q.awaiting_review || "—"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

const AssessmentCard = ({ assessment, names, isCohort }) => {
  const [view, setView] = useState("students");
  const s = assessment.summary;
  return (
    <Card>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 17, fontFamily: FONT }}>{assessment.title}</Typography>
          <Typography sx={{ fontSize: 13, color: INK.muted }}>
            {plural(assessment.question_count, "question")} · {number(assessment.max_score)} points
            {assessment.description ? ` · ${assessment.description}` : ""}
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="students" sx={{ textTransform: "none" }}>{isCohort ? "Students" : "Answers"}</ToggleButton>
          <ToggleButton value="questions" sx={{ textTransform: "none" }}>Questions</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <KpiRow>
        {isCohort && <Kpi label="Started" value={`${s.started}/${s.students}`} />}
        {isCohort && <Kpi label="Completed" value={`${s.completed}/${s.students}`} />}
        <Kpi label={isCohort ? "Average score" : "Score"} value={percent(s.average_percent)}
             hint={s.awaiting_review > 0
               ? "unmarked answers count as 0"
               : isCohort && s.median_percent !== null ? `median ${percent(s.median_percent)}` : null} />
        {isCohort && <Kpi label="Best score" value={percent(s.best_percent)} />}
        <Kpi label={isCohort ? "Median active time" : "Active time"} value={duration(s.median_active_seconds)} />
        <Kpi label={isCohort ? "Avg attempts" : "Attempts"} value={number(s.average_attempts)} />
        <Kpi label="To review" value={s.awaiting_review} />
      </KpiRow>
      {view === "students"
        ? <StudentsTable assessment={assessment} names={names} isCohort={isCohort} />
        : <QuestionsTable assessment={assessment} />}
    </Card>
  );
};

// ─── Assignments ───────────────────────────────────────────────────────────

const ReviewDialog = ({ open, onClose, onSave, row, name }) => {
  const [status, setStatus] = useState("reviewed");
  const [score, setScore] = useState("");
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && row) {
      setStatus(row.status === "needs_revision" ? "needs_revision" : "reviewed");
      setScore(row.score ?? "");
      setFeedback(row.feedback || "");
      setError("");
    }
  }, [open, row]);

  const save = async () => {
    const n = score === "" ? "" : Number(score);
    if (n !== "" && (Number.isNaN(n) || n < 0 || n > 100)) {
      setError("Score must be between 0 and 100.");
      return;
    }
    setSaving(true);
    try {
      await onSave({ status, score: n, feedback });
    } catch (e) {
      setError(subsApi.errorMessage(e, "Couldn't save the review."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontFamily: FONT }}>Review {name}’s submission</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: INK.muted, mb: 1 }}>
          {row?.file_name} · {fileSize(row?.file_size)} · submitted {date(row?.last_submitted_at)}
          {row?.attempts > 1 ? ` (${plural(row.attempts, "submission")})` : ""}
        </Typography>
        <RadioGroup row value={status} onChange={(e) => setStatus(e.target.value)}>
          <FormControlLabel value="reviewed" control={<Radio size="small" />} label="Reviewed" />
          <FormControlLabel value="needs_revision" control={<Radio size="small" />} label="Needs revision" />
        </RadioGroup>
        <TextField label="Score (out of 100, optional)" size="small" type="number" value={score}
                   onChange={(e) => setScore(e.target.value)} inputProps={{ min: 0, max: 100, step: 0.5 }}
                   sx={{ mt: 1, mb: 2, width: 240 }} />
        <TextField label="Feedback for the student" multiline minRows={3} fullWidth value={feedback}
                   onChange={(e) => setFeedback(e.target.value)} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving} sx={{ textTransform: "none", backgroundColor: "#0172A6" }}>
          Save review
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AssignmentCard = ({ assignment, names, isCohort, kind, moduleId, onReviewed, notify }) => {
  const [reviewing, setReviewing] = useState(null);
  const rows = useMemo(
    () => assignment.students.map((r) => ({ ...r, name: names[r.student_id] || "Student" })),
    [assignment, names],
  );
  const [sorted, by] = useSort(rows, { key: "name", dir: "asc" });
  const s = assignment.summary;

  const download = async (row) => {
    try {
      const { download_url } = await subsApi.assignmentFileUrl(kind, moduleId, row.submission_id);
      if (download_url) window.open(download_url, "_blank", "noopener");
    } catch (e) {
      notify(subsApi.errorMessage(e, "Couldn't download."));
    }
  };

  const save = async (review) => {
    await subsApi.reviewAssignment(kind, moduleId, reviewing.submission_id, review);
    setReviewing(null);
    onReviewed();
  };

  return (
    <Card>
      <Typography sx={{ fontWeight: 600, fontSize: 17, fontFamily: FONT }}>{assignment.title}</Typography>
      <Typography sx={{ fontSize: 13, color: INK.muted, mb: 1.5 }}>
        {assignment.file_name} · v{assignment.current_version} · given {date(assignment.published_at)}
      </Typography>
      <KpiRow>
        <Kpi label="Submitted" value={isCohort ? `${s.submitted}/${s.students}` : s.submitted ? "Yes" : "No"}
             hint={isCohort ? percent(s.submitted_percent) : null} />
        <Kpi label="To review" value={s.awaiting_review} />
        <Kpi label="Reviewed" value={s.reviewed} />
        <Kpi label="Needs revision" value={s.needs_revision} />
        <Kpi label={isCohort ? "Average score" : "Score"} value={number(s.average_score)} hint="out of 100" />
        <Kpi label={isCohort ? "Median days to submit" : "Days to submit"} value={number(s.median_days_to_submit)} />
        <Kpi label={isCohort ? "Avg submissions" : "Submissions"} value={number(s.average_attempts)} />
      </KpiRow>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" aria-label={`${assignment.title}: submissions`}>
          <TableHead>
            <TableRow>
              {isCohort && <TableCell sx={head}>{by("name", "Student", "asc")}</TableCell>}
              <TableCell sx={head}>Status</TableCell>
              <TableCell sx={head}>{by("last_submitted_at", "Submitted")}</TableCell>
              <TableCell sx={head}>{by("days_to_submit", "Days to submit", "asc")}</TableCell>
              <TableCell sx={head}>{by("attempts", "Submissions")}</TableCell>
              <TableCell sx={head}>File</TableCell>
              <TableCell sx={head}>{by("score", "Score")}</TableCell>
              <TableCell sx={head}>Feedback</TableCell>
              <TableCell sx={head} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((r) => {
              const submitted = r.status !== "not_submitted";
              return (
                <TableRow key={r.student_id} hover data-testid="submission-row">
                  {isCohort && <TableCell sx={{ ...cell, fontWeight: 500 }}>{r.name}</TableCell>}
                  <TableCell sx={cell}><StatusChip status={r.status} /></TableCell>
                  <TableCell sx={cell}>{submitted ? date(r.last_submitted_at) : "—"}</TableCell>
                  <TableCell sx={cell}>{submitted ? number(r.days_to_submit) : "—"}</TableCell>
                  <TableCell sx={cell}>{submitted ? r.attempts : "—"}</TableCell>
                  <TableCell sx={cell}>
                    {submitted && (
                      <Tooltip title={`Download ${r.file_name}`}>
                        <IconButton size="small" aria-label={`Download ${r.name}'s file`} onClick={() => download(r)}>
                          <DownloadOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={cell}>{r.score ?? "—"}</TableCell>
                  <TableCell sx={{ ...cell, maxWidth: 220, color: INK.secondary }}>
                    <Typography sx={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.feedback || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={cell} align="right">
                    {submitted && (
                      <Button size="small" onClick={() => setReviewing(r)} sx={{ textTransform: "none", whiteSpace: "nowrap" }}>
                        {r.status === "submitted" ? "Review" : "Edit review"}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      <ReviewDialog open={Boolean(reviewing)} row={reviewing} name={reviewing?.name}
                    onClose={() => setReviewing(null)} onSave={save} />
    </Card>
  );
};

// ─── The report ────────────────────────────────────────────────────────────

const ModuleReport = ({ kind, moduleId, initialTab = "assessments", notify = () => {} }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(initialTab === "assignments" ? "assignments" : "assessments");

  const load = useCallback(async () => {
    setError("");
    try {
      setData(await subsApi.getModuleReport(kind, moduleId));
    } catch (e) {
      setError(subsApi.errorMessage(e, "Couldn't load the submissions."));
    }
  }, [kind, moduleId]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const names = useMemo(
    () => Object.fromEntries((data?.students || []).map((s) => [s.id, s.name])),
    [data],
  );

  if (error) return <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{error}</Alert>;
  if (!data) return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;

  const isCohort = kind === "template";
  const toReview = (data.assessments || []).reduce((n, a) => n + a.summary.awaiting_review, 0);
  const assignmentsToReview = (data.assignments || []).reduce((n, a) => n + a.summary.awaiting_review, 0);

  return (
    <Box>
      <Typography sx={{ fontSize: 22, fontWeight: 600, fontFamily: FONT }}>{data.module.name}</Typography>
      <Typography sx={{ fontSize: 13, color: INK.muted, mb: 1 }}>
        {isCohort ? `Cohort module · ${plural(data.students.length, "student")}` : `1:1 · ${data.students[0]?.name || ""}`}
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: "1px solid #e4eaf0" }}>
        <Tab value="assessments" sx={{ textTransform: "none" }}
             label={`Assessments (${data.assessments.length})${toReview ? ` · ${toReview} to review` : ""}`} />
        <Tab value="assignments" sx={{ textTransform: "none" }}
             label={`Assignments (${data.assignments.length})${assignmentsToReview ? ` · ${assignmentsToReview} to review` : ""}`} />
      </Tabs>

      {tab === "assessments" && (data.assessments.length === 0
        ? <Empty>No assessments in this module yet. Add one from the module’s Task Lists → Manage assessments.</Empty>
        : data.assessments.map((a) => (
          <AssessmentCard key={a.id ?? "other"} assessment={a} names={names} isCohort={isCohort} />
        )))}

      {tab === "assignments" && (data.assignments.length === 0
        ? <Empty>No assignments in this module yet. Add one from the module’s Task Lists → Manage assignments.</Empty>
        : data.assignments.map((a) => (
          <AssignmentCard key={a.id} assignment={a} names={names} isCohort={isCohort} kind={kind}
                          moduleId={moduleId} onReviewed={() => { notify("Review saved."); load(); }} notify={notify} />
        )))}
    </Box>
  );
};

export default ModuleReport;
